// バックエンド信頼性 フェーズ0 — 稼働構成の実測と有効化フラグ（管理者のみ）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowsClockwise, Warning, CheckCircle, Lock } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { jstDateTime } from '@/utils/jstDate';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/reliability`;

const Pill = ({ tone = 'zinc', children, testId }) => (
  <span data-testid={testId}
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${{
      teal: 'border-teal-200 bg-teal-50 text-teal-800 dark:border-teal-800 dark:bg-teal-950 dark:text-teal-200',
      amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
      rose: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-200',
      zinc: 'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
    }[tone]}`}>{children}</span>
);

const Card = ({ title, subtitle, action, children, testid }) => (
  <section data-testid={testid}
    className="rounded-lg border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
    <div className="flex flex-wrap items-start justify-between gap-2 pb-2">
      <div>
        <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && <p className="text-[11.5px] text-zinc-500 dark:text-zinc-400">{subtitle}</p>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const PHASE_LABEL = {
  1: 'フェーズ1 · 重要ジョブの永続化',
  2: 'フェーズ2 · 実行の固定化と保護',
  3: 'フェーズ3 · データ整合性',
  4: 'フェーズ4 · 検索の健全性',
};

export default function ReliabilitySection() {
  const [topo, setTopo] = useState(null);
  const [diag, setDiag] = useState(null);
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const [t, d, f] = await Promise.all([
        axios.get(`${BASE}/topology`, { withCredentials: true }),
        axios.get(`${BASE}/diagnostics`, { withCredentials: true }),
        axios.get(`${BASE}/flags`, { withCredentials: true }),
      ]);
      setTopo(t.data);
      setDiag(d.data);
      setFlags(f.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || '信頼性診断を取得できませんでした');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (id, next) => {
    setBusy(id);
    try {
      const r = await axios.put(`${BASE}/flags`,
        { flags: { [id]: next }, expected_version: flags?.version ?? null },
        { withCredentials: true });
      setFlags(r.data);
      toast.success(next ? '有効にしました' : '無効にしました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '更新できませんでした');
      await load();
    } finally { setBusy(''); }
  };

  if (!topo) return null;
  const multi = topo.multi_replica_observed;
  const tasks = (diag?.task_registries || []).filter((r) => r.kind === 'tasks');
  const states = diag?.state_registries || [];
  const grouped = {};
  (flags?.definitions || []).forEach((d) => {
    grouped[d.phase] = grouped[d.phase] || [];
    grouped[d.phase].push(d);
  });

  return (
    <div className="space-y-4" data-testid="reliability-section">
      <Card testid="reliability-topology" title="稼働構成の実測 / Deployment topology"
        subtitle="推測ではなく、各プロセスが書き込むハートビートから算出しています。"
        action={(
          <Button variant="outline" className="h-10 text-[12px]" onClick={load}
            data-testid="reliability-refresh">
            <ArrowsClockwise size={13} className="mr-1" />更新
          </Button>
        )}>
        <div className="flex flex-wrap gap-1.5" data-testid="reliability-topology-summary">
          <Pill tone={multi ? 'amber' : 'teal'} testId="reliability-replica-count">
            稼働プロセス {topo.replica_count}
          </Pill>
          <Pill tone={topo.mixed_version_observed ? 'amber' : 'zinc'}>
            配置バージョン {(topo.distinct_git_revs || []).length || 0}種
          </Pill>
          <Pill tone={topo.self_heartbeat_recorded ? 'teal' : 'amber'}>
            自プロセス記録 {topo.self_heartbeat_recorded ? '済' : '未'}
          </Pill>
          <Pill>ハートビート {topo.heartbeat?.interval_seconds}秒 / 失効 {topo.heartbeat?.ttl_seconds}秒</Pill>
          <Pill tone={topo.heartbeat?.failures ? 'rose' : 'zinc'}>
            書き込み {topo.heartbeat?.writes ?? 0} / 失敗 {topo.heartbeat?.failures ?? 0}
          </Pill>
        </div>

        <p className={`mt-3 flex items-start gap-1.5 rounded-md border px-2 py-1.5 text-[11.5px] ${multi
          ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200'
          : 'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'}`}
          data-testid="reliability-topology-note">
          {multi ? <Warning size={14} className="mt-0.5 shrink-0" />
            : <CheckCircle size={14} className="mt-0.5 shrink-0" />}
          <span>{topo.note_ja}</span>
        </p>

        <div className="overflow-x-auto pt-3">
          <table className="w-full min-w-[560px] text-[11.5px]" data-testid="reliability-instances">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400">
                <th className="py-1 font-medium">プロセス</th>
                <th className="py-1 font-medium">ホスト</th>
                <th className="py-1 font-medium">起動</th>
                <th className="py-1 font-medium">版</th>
                <th className="py-1 font-medium">処理中</th>
                <th className="py-1 font-medium">最終確認</th>
              </tr>
            </thead>
            <tbody>
              {(topo.instances || []).map((r) => (
                <tr key={r.instance_id} className="border-t border-zinc-100 dark:border-zinc-800"
                  data-testid={`reliability-instance-${r.instance_id}`}>
                  <td className="py-1 text-zinc-800 dark:text-zinc-200">
                    {r.instance_id}
                    {r.instance_id === topo.self?.instance_id && (
                      <span className="ml-1 text-teal-600">（このプロセス）</span>)}
                    <span className="text-zinc-400"> pid {r.pid}</span>
                  </td>
                  <td className="py-1 text-zinc-600 dark:text-zinc-400">{r.hostname || '—'}</td>
                  <td className="py-1 text-zinc-600 dark:text-zinc-400">
                    {jstDateTime(r.boot_at || '', true)}
                  </td>
                  <td className="py-1 text-zinc-600 dark:text-zinc-400">{r.git_rev || '—'}</td>
                  <td className="py-1 tabular-nums text-zinc-600 dark:text-zinc-400">
                    {r.in_flight?.background_tasks ?? 0}
                  </td>
                  <td className="py-1 text-zinc-600 dark:text-zinc-400">
                    {String(r.last_seen || '').slice(11, 19)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card testid="reliability-diagnostics" title="プロセス内だけの作業と調整状態 / Process-local state"
        subtitle="ここに数字が出ている作業は、このプロセスが停止・再デプロイされると失われます。">
        <div className="flex flex-wrap gap-1.5 pb-2">
          <Pill tone={(diag?.in_flight?.background_tasks || 0) > 0 ? 'amber' : 'teal'}
            testId="reliability-inflight-total">
            バックグラウンド処理 合計 {diag?.in_flight?.background_tasks ?? 0}
          </Pill>
          <Pill>画像モデル使用中 {diag?.in_flight?.vision_busy_models ?? 0}</Pill>
          <Pill tone={(diag?.in_flight?.vision_waiting || 0) > 0 ? 'amber' : 'zinc'}>
            順番待ち {diag?.in_flight?.vision_waiting ?? 0}
          </Pill>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-[11.5px]" data-testid="reliability-task-registries">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400">
                <th className="py-1 font-medium">保持している作業</th>
                <th className="py-1 font-medium">件数</th>
                <th className="py-1 font-medium">場所</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((r) => (
                <tr key={`${r.module}.${r.attribute}`}
                  className="border-t border-zinc-100 dark:border-zinc-800"
                  data-testid={`reliability-task-${r.attribute}`}>
                  <td className="py-1 text-zinc-800 dark:text-zinc-200">{r.label_ja}</td>
                  <td className="py-1 tabular-nums text-zinc-800 dark:text-zinc-200">
                    {r.module_loaded ? (r.size ?? 0) : '未読込'}
                  </td>
                  <td className="py-1 text-zinc-400">{r.module}.{r.attribute}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="space-y-1 pt-3" data-testid="reliability-state-registries">
          {states.map((r) => (
            <div key={`${r.module}.${r.attribute}`}
              className="rounded-md border border-zinc-200 px-2 py-1.5 text-[11px] dark:border-zinc-700">
              <p className="text-zinc-800 dark:text-zinc-200">
                {r.label_ja}
                <span className="text-zinc-400"> · {r.module_loaded ? `${r.size ?? 0}件` : '未読込'}</span>
              </p>
              <p className="text-zinc-500 dark:text-zinc-400">{r.multi_replica_risk_ja}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card testid="reliability-flags" title="有効化フラグ / Activation flags"
        subtitle="すべて既定は無効です。無効の間は従来どおりの動作のままで、後続フェーズの新しい経路は使われません。">
        <div className="space-y-3" data-testid="reliability-flag-list">
          {Object.keys(grouped).sort().map((phase) => (
            <div key={phase}>
              <p className="pb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                {PHASE_LABEL[phase] || `フェーズ${phase}`}
              </p>
              <div className="space-y-1.5">
                {grouped[phase].map((d) => (
                  <div key={d.id}
                    className="flex items-start justify-between gap-3 rounded-md border border-zinc-200 px-2.5 py-2 dark:border-zinc-700"
                    data-testid={`reliability-flag-${d.id}`}>
                    <div className="min-w-0">
                      <p className="flex items-center gap-1 text-[12.5px] text-zinc-900 dark:text-zinc-100">
                        {d.blocked && <Lock size={12} className="text-rose-500" />}
                        {d.label_ja}
                        <span className="text-[10.5px] text-zinc-400">/ {d.label_en}</span>
                      </p>
                      <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{d.desc_ja}</p>
                      {d.blocked && (
                        <p className="pt-0.5 text-[11px] text-rose-600 dark:text-rose-300"
                          data-testid={`reliability-flag-blocked-${d.id}`}>
                          未対応：{d.blocked_reason_ja}
                        </p>
                      )}
                    </div>
                    <Switch checked={!!d.value} disabled={!!d.blocked || busy === d.id}
                      onCheckedChange={(v) => toggle(d.id, v)}
                      data-testid={`reliability-flag-switch-${d.id}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="pt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
          設定の版 {flags?.version ?? 0}
          {flags?.updated_at ? ` · 最終更新 ${jstDateTime(flags.updated_at, true)}` : ''}
          {flags?.updated_by ? ` · ${flags.updated_by}` : ''}
        </p>
      </Card>
    </div>
  );
}
